/**
 * What the buyer pays, worked out on the listing page (P25009-482 BR-1/2/5/6).
 *
 * The buy box prints the price for the chosen quantity, one price per
 * delivery choice, and a total that must equal the checkout total to the öre.
 * This module is the listing-page half of that promise: it mirrors the
 * server's line-item rules (server/api-util/lineItems.js) closely enough to
 * print a number instantly, and the buy box then confirms it with the
 * server's own line items (/api/transaction-line-items — the same function
 * checkout prices with), which win whenever they arrive.
 *
 * Mirrored rules, each with its server counterpart:
 *   item      listed price × quantity. The price is VAT-inclusive, so VAT
 *             never adds to the total; it is only split out of it.
 *   VAT       any goods from a company seller, none from a private one
 *             (P25009-468): price − the rounded net at 25 %, per unit
 *             (isVatApplicable / getVATLineItem)
 *   freight   one-item price + extra-item price × (quantity − 1), where no
 *             extra-item price means the one-item price (calculateShippingFee).
 *             The freight matrix (FREIGHT_MATRIX_CHECKOUT_ENABLED) is a server
 *             flag the browser cannot see — the server's line items cover it.
 *   services  only those that apply to the listing, each by its own price
 *             unit (serviceTargetingCore, P25009-484). The buy box prices the
 *             ones checkout adds by itself: montering on new complete wheels
 *   miljöavgift 25 kr per tyre on new tyres and new complete wheels
 *             (serviceTargetingCore, P25009-484 BR-6)
 *   varav moms the VAT of the whole order, as checkout prints it under its
 *             total (P25009-475, orderVatCore): the item VAT above, plus the
 *             VAT inside freight, services and the miljöavgift when the
 *             seller is a company
 *
 * Quantity is always in SALES units (a set listing sells sets), the invariant
 * salesUnit.js documents; wheel counts come from physicalItemCount.
 */
import {
  SALES_UNIT_SET,
  defaultOrderQuantity,
  getUnitNoun,
  pieceAmount,
  resolveSalesUnit,
} from './salesUnit';
import { physicalItemCount } from './freightCore';
import {
  autoServiceIds,
  miljoavgiftSubunits,
  servicesForListing,
  serviceSubunits,
} from './serviceTargetingCore';
import { buyerPrices, isVatRegisteredSeller, vatInside } from './orderVatCore';

export const DELIVERY_SHIPPING = 'shipping';
export const DELIVERY_PICKUP = 'pickup';

const VAT_RATE = 25;

/* Upper bound for the stepper when a listing does not track stock. */
const UNTRACKED_STOCK_MAX = 12;

const num = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Same rule as the server's isVatApplicable: only a company seller charges VAT,
 * new or used goods alike (P25009-468). */
export const carriesVat = (publicData = {}, author = null) => isVatRegisteredSeller(author);

/** VAT inside one unit's price, in subunits — the server's rounding. */
export const unitVatSubunits = (unitAmount, publicData, author) => {
  if (!carriesVat(publicData, author) || !(unitAmount > 0)) return 0;
  return unitAmount - Math.round(unitAmount / (1 + VAT_RATE / 100));
};

/** Mirror of calculateShippingFee: freight for `quantity` sales units, in subunits. */
export const shippingSubunitsFor = (publicData = {}, quantity) => {
  const one = num(publicData.shippingPriceInSubunitsOneItem);
  const additionalRaw = publicData.shippingPriceInSubunitsAdditionalItems;
  if (!one || !(quantity >= 1)) return 0;
  if (quantity === 1) return one;
  const additional =
    additionalRaw === null || additionalRaw === undefined ? one : num(additionalRaw) || 0;
  return one + additional * (quantity - 1);
};

/**
 * The stepper's range. A set listing is locked at one set (P25009-474 BR-4);
 * a per-item listing runs 1..stock.
 */
export const quantityBounds = (publicData = {}, stock) => {
  const { salesUnit } = resolveSalesUnit(publicData);
  if (salesUnit === SALES_UNIT_SET) return { min: 1, max: 1, locked: true };
  const tracked = typeof stock === 'number' && Number.isFinite(stock);
  const max = tracked ? Math.max(1, stock) : UNTRACKED_STOCK_MAX;
  return { min: 1, max, locked: false };
};

/** Default 4 (the set size) for per-item listings, capped by stock (474 BR-2). */
export const initialQuantity = (publicData = {}, stock) => {
  const tracked = typeof stock === 'number' && Number.isFinite(stock) ? stock : null;
  const { max } = quantityBounds(publicData, tracked);
  return Math.max(1, Math.min(defaultOrderQuantity(publicData, tracked), max));
};

/**
 * The ways this listing can reach the buyer, in the buy box's order:
 * Hemleverans, then Hämta hos {säljare} (P25009-484 BR-4).
 *
 * Pickup is ALWAYS offered: some listings had no pickup option at all, and a
 * buyer can always collect from the seller. Hemleverans needs the listing to
 * ship — without that there is no freight price to charge.
 *
 * "Monterat hos {säljare}" is no longer a delivery option: montering is a
 * service (BR-5), added in checkout on new complete wheels.
 */
export const deliveryOptionsFor = ({ publicData = {} }) =>
  [publicData.shippingEnabled ? { key: DELIVERY_SHIPPING } : null, { key: DELIVERY_PICKUP }].filter(
    Boolean
  );

/** The choice the buy box starts on: home delivery when offered, else pickup. */
export const defaultDeliveryOption = options =>
  (options || []).find(o => o.key === DELIVERY_SHIPPING) ||
  (options || []).find(o => o.key === DELIVERY_PICKUP) ||
  (options || [])[0] ||
  null;

/** What checkout needs to open with the same choice (BR-5 → orderData). */
export const orderDataForDelivery = option => {
  if (!option) return {};
  if (option.key === DELIVERY_SHIPPING) return { deliveryMethod: 'shipping' };
  return { deliveryMethod: 'pickup' };
};

/** Delivery price of one option for this quantity, in subunits. Pickup is free. */
export const deliverySubunitsFor = ({ option, publicData = {}, quantity }) => {
  if (!option) return 0;
  if (option.key === DELIVERY_SHIPPING) return shippingSubunitsFor(publicData, quantity);
  return 0;
};

/** This listing at this quantity, as one order line for serviceTargetingCore. */
export const orderLineFor = (listing, quantity) => {
  const publicData = listing?.attributes?.publicData || {};
  return {
    publicData,
    metadata: listing?.attributes?.metadata || {},
    physicalItems: physicalItemCount({ quantity, ...resolveSalesUnit(publicData) }),
  };
};

/**
 * The services checkout adds by itself for this listing (P25009-484 BR-5:
 * montering on new complete wheels), so the buy box total already holds them
 * and matches checkout. The buyer can still remove them there.
 *
 * @returns {Array} the seller's service documents
 */
export const autoServicesFor = listing => {
  const services = servicesForListing(
    listing?.author?.attributes?.profile?.publicData?.services,
    listing?.attributes?.publicData || {}
  );
  const ids = autoServiceIds(services, [orderLineFor(listing, 1)]);
  return services.filter(s => ids.includes(s.id));
};

/**
 * Everything the buy box prints, from the listing alone.
 *
 * @returns {null|{ quantity, wheels, noun, unitAmount, pieceAmount, itemsSubunits,
 *   deliverySubunits, totalSubunits, vatSubunits, hasVat, currency }}
 */
export const listingOrderSummary = ({ listing, quantity, option }) => {
  const price = listing?.attributes?.price;
  const publicData = listing?.attributes?.publicData || {};
  if (!price?.amount) return null;
  const author = listing?.author || null;
  const resolved = resolveSalesUnit(publicData);
  const qty = Math.max(1, Number.parseInt(quantity, 10) || 1);
  const lines = [orderLineFor(listing, qty)];

  const itemsSubunits = price.amount * qty;
  const deliverySubunits = deliverySubunitsFor({ option, publicData, quantity: qty });
  const services = autoServicesFor(listing).map(service => ({
    id: service.id,
    title: service.title,
    subunits: serviceSubunits(service, lines),
  }));
  const servicesSubunits = services.reduce((sum, s) => sum + s.subunits, 0);
  const miljoavgift = miljoavgiftSubunits(lines);

  // Freight, each service and the miljöavgift are one line each on the
  // server, so each is rounded on its own, as orderVatCore does.
  const extraVatSubunits = isVatRegisteredSeller(author)
    ? [deliverySubunits, ...services.map(s => s.subunits), miljoavgift].reduce(
        (sum, amount) => sum + vatInside(amount),
        0
      )
    : 0;
  const vatSubunits = unitVatSubunits(price.amount, publicData, author) * qty + extraVatSubunits;

  return {
    quantity: qty,
    wheels: physicalItemCount({ quantity: qty, ...resolved }),
    noun: getUnitNoun(publicData),
    unitAmount: price.amount,
    pieceAmount: pieceAmount(price.amount, resolved),
    itemsSubunits,
    deliverySubunits,
    services,
    servicesSubunits,
    miljoavgiftSubunits: miljoavgift,
    totalSubunits: itemsSubunits + deliverySubunits + servicesSubunits + miljoavgift,
    vatSubunits,
    hasVat: carriesVat(publicData, author),
    currency: price.currency,
  };
};

/**
 * The server's answer, read out of the line items /api/transaction-line-items
 * returns (Money objects with lineTotal, as constructValidLineItems builds
 * them). The customer's side is what checkout charges, and its VAT is what
 * checkout prints as "varav moms".
 *
 * @param {Array} lineItems
 * @param {Object} [options]
 * @param {boolean} [options.sellerChargesVat] the seller is a company (orderVatCore)
 * @returns {null|{ totalSubunits, vatSubunits, deliverySubunits, servicesSubunits,
 *   miljoavgiftSubunits }}
 */
export const summaryFromLineItems = (lineItems, { sellerChargesVat = false } = {}) => {
  if (!Array.isArray(lineItems) || lineItems.length === 0) return null;
  const customerLines = lineItems.filter(
    item => !item.reversal && (item.includeFor || []).includes('customer')
  );
  const amountOf = item => item?.lineTotal?.amount || 0;
  const sumOf = codes =>
    customerLines.filter(item => codes.includes(item.code)).reduce((s, i) => s + amountOf(i), 0);
  return {
    totalSubunits: customerLines.reduce((s, i) => s + amountOf(i), 0),
    vatSubunits: buyerPrices(lineItems, { sellerChargesVat }).vatSubunits,
    deliverySubunits: sumOf(['line-item/shipping-fee', 'line-item/pickup-fee']),
    servicesSubunits: sumOf(['line-item/service']),
    miljoavgiftSubunits: sumOf(['line-item/miljoavgift']),
  };
};
